/**
 * Paginated list endpoints (`/leads`, `/tasks`, …) answer with
 * `{ data, current_page, page_size, total_pages, total }` and a **default
 * `page_size` of 20**.
 *
 * Any client-side aggregate — a sum, an average, a ranking, a "top N" — is
 * wrong if it stops after the first page: it silently describes a slice while
 * looking like a total. `fetchAllPages` walks the pages instead.
 *
 * See docs/dashboards-endpoints.md ("counts-only report") for why the sales
 * dashboards aggregate client-side in the first place.
 */

/** Pages fetched at most, so one partner with a huge history can't hang a card. */
export const DEFAULT_MAX_PAGES = 10;

type PageBody = {
  links?: { next?: string | null };
  total_pages?: number | null;
  current_page?: number | null;
};

/**
 * Prefer `links.next` — `skip_total_size=1` leaves `total` / `total_pages` null.
 * Fall back to `total_pages > current_page` for endpoints that still send counts.
 */
export function hasNextPage(body?: PageBody | null): boolean {
  if (body?.links?.next) return true;
  const totalPages = body?.total_pages;
  const currentPage = body?.current_page;
  return (
    typeof totalPages === 'number' &&
    typeof currentPage === 'number' &&
    totalPages > currentPage
  );
}

export type PagedResult<T> = {
  items: T[];
  /** True when `maxPages` cut the walk short — the aggregate understates. */
  truncated: boolean;
  /** Server-reported row count, when it sends one. */
  total: number | null;
};

/**
 * Collects every page of a paginated endpoint.
 *
 * @param fetchPage receives the 1-based page number and must resolve the raw
 *   `request` result (`{data: {data, total_pages, …}}`).
 */
export const fetchAllPages = async <T>(
  fetchPage: (page: number) => Promise<any>,
  { maxPages = DEFAULT_MAX_PAGES }: { maxPages?: number } = {},
): Promise<PagedResult<T>> => {
  const items: T[] = [];
  let total: number | null = null;
  let totalPages = 1;
  let page = 1;

  // Sequential on purpose: page 1 is what tells us how many pages there are.
  for (; page <= totalPages && page <= maxPages; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result: any = await fetchPage(page);
    const body = result?.data;
    items.push(...((body?.data || []) as T[]));
    if (page === 1) {
      totalPages = Number(body?.total_pages) || 1;
      total = body?.total !== undefined && body?.total !== null ? Number(body.total) : null;
    }
  }

  return { items, truncated: totalPages > maxPages, total };
};
